import { resolveLootModelKey } from './lootModelRegistry.mjs';
import {
  LOOT_CLIENT_FRESH_AGE_MS,
  LOOT_MAX_ACCURACY_METERS,
  LOOT_RADIUS_METERS,
} from './locationPolicy.mjs';

export const DEFAULT_PLAYER_POSITION = Object.freeze({ latitude: 51.0504, longitude: 13.7373 });
export const MAX_LOOT_DISTANCE_METERS = LOOT_RADIUS_METERS;
export const MAX_GPS_ACCURACY_METERS = LOOT_MAX_ACCURACY_METERS;
export const MAX_GPS_AGE_MS = LOOT_CLIENT_FRESH_AGE_MS;
export const GPS_RUN_ENTER_MPS = 2.2;
export const GPS_RUN_EXIT_MPS = 1.5;
export const GPS_WALK_ENTER_MPS = 0.28;
export const GPS_MAX_PLAUSIBLE_MPS = 12;

export function haversineDistanceMeters(a, b) {
  const lat1 = Number(a?.latitude ?? a?.lat);
  const lon1 = Number(a?.longitude ?? a?.lng);
  const lat2 = Number(b?.latitude ?? b?.lat);
  const lon2 = Number(b?.longitude ?? b?.lng);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  const toRadians = (value) => (value * Math.PI) / 180;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const value = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

export function smoothPosition(previous, next) {
  if (!previous) return next;
  const accuracy = Math.max(1, Number(next?.accuracy || 30));
  const factor = accuracy <= 15 ? 0.72 : accuracy <= 50 ? 0.48 : 0.3;
  return { ...next, latitude: previous.latitude + (next.latitude - previous.latitude) * factor, longitude: previous.longitude + (next.longitude - previous.longitude) * factor };
}

export function normalizeBrowserPosition(position, receivedAt = Date.now()) {
  const latitude = Number(position?.coords?.latitude);
  const longitude = Number(position?.coords?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)
    || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  const accuracy = Number(position?.coords?.accuracy);
  const heading = Number(position?.coords?.heading);
  const speed = Number(position?.coords?.speed);
  const deviceTimestamp = Number(position?.timestamp || receivedAt);
  return {
    latitude,
    longitude,
    accuracy: Number.isFinite(accuracy) && accuracy >= 0 && accuracy <= 100000 ? accuracy : null,
    heading: Number.isFinite(heading) ? heading : null,
    speed: Number.isFinite(speed) ? speed : null,
    timestamp: receivedAt,
    deviceTimestamp: Number.isFinite(deviceTimestamp) && deviceTimestamp > 0 ? deviceTimestamp : receivedAt,
    receivedAt,
    source: 'gps',
  };
}

export function positionStatus(position) {
  if (!position) return 'requesting';
  const accuracy = Number(position.accuracy);
  return position.accuracy === null || position.accuracy === undefined
    || !Number.isFinite(accuracy) || accuracy < 0 || accuracy > MAX_GPS_ACCURACY_METERS ? 'inaccurate' : 'active';
}

export function getLootEligibilityState({
  authenticated = true,
  position,
  location,
  now = Date.now(),
  cooldownMs = 0,
  isLooting = false,
  gpsStatus = '',
  isSecureContext = true,
} = {}) {
  if (isLooting) return { eligible: false, reason: 'loading', distance: Number.POSITIVE_INFINITY };
  if (!authenticated) return { eligible: false, reason: 'logged_out', distance: Number.POSITIVE_INFINITY };
  if (!isSecureContext) return { eligible: false, reason: 'gps_https_required', distance: Number.POSITIVE_INFINITY };
  if (!location) return { eligible: false, reason: 'location_unavailable', distance: Number.POSITIVE_INFINITY };
  if (!position || position.source !== 'gps') {
    const statusReason = ({ denied: 'gps_denied', timeout: 'gps_timeout', unavailable: 'location_unavailable', requesting: 'gps_loading' })[gpsStatus];
    return { eligible: false, reason: statusReason || 'gps_off', distance: Number.POSITIVE_INFINITY };
  }
  const accuracy = Number(position.accuracy);
  if (!Number.isFinite(accuracy) || accuracy > MAX_GPS_ACCURACY_METERS) {
    return { eligible: false, reason: 'gps_inaccurate', accuracy, distance: Number.POSITIVE_INFINITY };
  }
  const timestamp = Number(position.timestamp);
  const ageMs = Number(now) - timestamp;
  if (!Number.isFinite(timestamp) || timestamp <= 0 || ageMs > MAX_GPS_AGE_MS) {
    return { eligible: false, reason: 'gps_stale', ageMs, distance: Number.POSITIVE_INFINITY };
  }
  if (Number(cooldownMs) > 0) {
    return { eligible: false, reason: 'cooldown', cooldownMs: Number(cooldownMs), distance: haversineDistanceMeters(position, location) };
  }
  const distance = haversineDistanceMeters(position, location);
  if (!Number.isFinite(distance)) return { eligible: false, reason: 'location_unavailable', distance };
  if (distance > MAX_LOOT_DISTANCE_METERS) return { eligible: false, reason: 'too_far', distance };
  return { eligible: true, reason: 'eligible', distance };
}

export function isLootEligible(options = {}) {
  return getLootEligibilityState(options).eligible;
}

export function circlePolygon(center, radiusMeters, points = 48) {
  const longitude = Number(center?.longitude);
  const latitude = Number(center?.latitude);
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude) || !(radiusMeters > 0)) return [];
  const coordinates = [];
  const latRadians = (latitude * Math.PI) / 180;
  for (let index = 0; index <= points; index += 1) {
    const bearing = (index / points) * Math.PI * 2;
    coordinates.push([
      longitude + (radiusMeters * Math.sin(bearing)) / (111320 * Math.max(0.2, Math.cos(latRadians))),
      latitude + (radiusMeters * Math.cos(bearing)) / 111320,
    ]);
  }
  return coordinates;
}

export function moveKeyboardPosition(position, northMeters, eastMeters) {
  const latitude = Number(position?.latitude ?? DEFAULT_PLAYER_POSITION.latitude);
  const longitude = Number(position?.longitude ?? DEFAULT_PLAYER_POSITION.longitude);
  return {
    ...position,
    latitude: latitude + Number(northMeters || 0) / 111320,
    longitude: longitude + Number(eastMeters || 0) / (111320 * Math.max(0.2, Math.cos((latitude * Math.PI) / 180))),
    accuracy: 0,
    timestamp: Date.now(),
    receivedAt: Date.now(),
    source: 'keyboard',
  };
}

export const moveDemoPosition = moveKeyboardPosition;

export function bearingDegrees(from, to) {
  const lat1 = Number(from?.latitude ?? from?.lat);
  const lon1 = Number(from?.longitude ?? from?.lng);
  const lat2 = Number(to?.latitude ?? to?.lat);
  const lon2 = Number(to?.longitude ?? to?.lng);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
  const toRadians = (value) => (value * Math.PI) / 180;
  const y = Math.sin(toRadians(lon2 - lon1)) * Math.cos(toRadians(lat2));
  const x = Math.cos(toRadians(lat1)) * Math.sin(toRadians(lat2))
    - Math.sin(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.cos(toRadians(lon2 - lon1));
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

export function inferGpsMotion(previous, next, previousMotion = 'idle') {
  if (!previous || !next) return { motion: 'idle', heading: Number(next?.heading) || 0, speedMps: 0, rejected: false };
  const elapsedSeconds = (Number(next.timestamp) - Number(previous.timestamp)) / 1000;
  const distance = haversineDistanceMeters(previous, next);
  if (!(elapsedSeconds > 0) || !Number.isFinite(distance)) {
    return { motion: 'idle', heading: Number(next?.heading) || Number(previous?.heading) || 0, speedMps: 0, rejected: true };
  }
  const derivedSpeed = distance / elapsedSeconds;
  const reportedSpeed = Number(next.speed);
  const speedMps = Number.isFinite(reportedSpeed) && reportedSpeed >= 0 ? Math.min(reportedSpeed, derivedSpeed * 1.8 + 0.8) : derivedSpeed;
  const rejected = derivedSpeed > GPS_MAX_PLAUSIBLE_MPS || distance > 80;
  if (rejected) return { motion: 'idle', heading: Number(previous?.heading) || 0, speedMps: derivedSpeed, rejected: true };

  let motion = 'idle';
  if (previousMotion === 'run' ? speedMps >= GPS_RUN_EXIT_MPS : speedMps >= GPS_RUN_ENTER_MPS) motion = 'run';
  else if (speedMps >= GPS_WALK_ENTER_MPS) motion = 'walk';
  const computedHeading = distance >= 0.8 ? bearingDegrees(previous, next) : null;
  return {
    motion,
    heading: Number.isFinite(Number(next.heading)) ? Number(next.heading) : (computedHeading ?? Number(previous.heading) ?? 0),
    speedMps,
    rejected: false,
  };
}

export function locationsToGeoJson(locations = [], readyModelKeys = new Set(), visibleModelLocationIds = null, selectedLocationId = null) {
  return {
    type: 'FeatureCollection',
    features: (Array.isArray(locations) ? locations : []).flatMap((location) => {
      const latitude = Number(location?.latitude ?? location?.lat);
      const longitude = Number(location?.longitude ?? location?.lng);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];
      const modelKey = resolveLootModelKey(location);
      const id = String(location?.id ?? location?.location_id ?? `${latitude}-${longitude}`);
      return [{
        type: 'Feature',
        id,
        geometry: { type: 'Point', coordinates: [longitude, latitude] },
        properties: {
          id: String(location?.id ?? location?.location_id ?? ''),
          title: String(location?.title ?? location?.name ?? 'Pfand-Ort'),
          category: String(location?.categoryLabel ?? location?.category ?? location?.categories?.[0] ?? 'Pfand-Ort'),
          modelKey: modelKey || '',
          modelReady: modelKey && readyModelKeys.has(modelKey) && (!visibleModelLocationIds || visibleModelLocationIds.has(id)) ? 1 : 0,
          selected: selectedLocationId != null && id === String(selectedLocationId) ? 1 : 0,
        },
      }];
    }),
  };
}
